/**
 * Jul-2026 (User-Wunsch) — Versand-Cockpit: alle offenen Versandaufträge der
 * Buchhaltung/Dispo an einem Ort mit 1-Klick-Labeldruck (Deutsche Post
 * Internetmarke) und Sendungsverfolgung. Aufruf: /dealer/versand
 */
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Package, Loader2, Printer, Stamp, ExternalLink, RefreshCw, Truck, MailCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { previewPdfResponse, previewPdfBlob } from "../lib/pdfPreview";
import { Button } from "../components/ui/button";
import EmployeeContextBanner from "../components/vermittler/EmployeeContextBanner";

const POSTAGE_PRODUCTS = [
    { value: "standardbrief", label: "Standardbrief (0,95 €)" },
    { value: "kompaktbrief", label: "Kompaktbrief (1,10 €)" },
    { value: "grossbrief", label: "Großbrief (1,80 €)" },
    { value: "maxibrief", label: "Maxibrief (2,90 €)" },
    { value: "einschreiben_einwurf", label: "Einschreiben Einwurf (3,30 €)" },
    { value: "einschreiben", label: "Einschreiben (3,80 €)" },
    { value: "einschreiben_rueckschein", label: "Einschreiben + Rückschein (6,00 €)" },
];

const trackingUrl = (t) => `https://www.deutschepost.de/de/s/sendungsverfolgung.html?piececode=${encodeURIComponent(t)}`;

function OpenRow({ row, onLabelCreated }) {
    const [product, setProduct] = useState("einschreiben_einwurf");
    const [busy, setBusy] = useState(false);
    const c = row.customer || {};
    const addrOk = Boolean(c.name && c.street && c.postal_code);

    const createLabel = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/listings/${row.id}/dispo/papers-shipment/internetmarke`, {
                product,
                recipient: { name: c.name, street: c.street, postal_code: c.postal_code, city: c.city },
            });
            const raw = atob(data.label_base64);
            const arr = new Uint8Array(raw.length);
            for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
            previewPdfBlob(new Blob([arr], { type: "application/pdf" }), `Porto-Label_${data.tracking_number}.pdf`);
            toast.success(`Label erstellt · ${data.tracking_number}${data.mocked ? " (TESTMODUS)" : ""}`);
            onLabelCreated?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Label konnte nicht erstellt werden");
        } finally { setBusy(false); }
    };

    return (
        <tr className="border-b border-gray-100 hover:bg-orange-50/40 transition-colors" data-testid={`versand-open-row-${row.id}`}>
            <td className="px-3 py-2.5">
                <Link to={`/listing/${row.id}?dispo=1`} className="font-bold text-[#111827] hover:text-orange-600 hover:underline text-[13px]" data-testid={`versand-open-vehicle-${row.id}`}>{row.title || "Fahrzeug"}</Link>
                {row.contract_number && <div className="text-[11px] text-gray-500 font-mono-num">KV {row.contract_number}</div>}
                <span className={`inline-block mt-0.5 px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider ${row.status === "verkauft" ? "bg-red-600 text-white" : row.status === "reserviert" ? "bg-orange-500 text-white" : "bg-yellow-400 text-[#111827]"}`}>{row.status}</span>
            </td>
            <td className="px-3 py-2.5 text-[12px]">
                <div className="font-semibold text-gray-800">{c.name || "—"}</div>
                <div className="text-gray-600">{c.street}</div>
                <div className="text-gray-600">{[c.postal_code, c.city].filter(Boolean).join(" ")}</div>
                {!addrOk && <div className="text-[11px] text-red-600 font-semibold mt-0.5">Adresse unvollständig — bitte Kundendaten ergänzen</div>}
            </td>
            <td className="px-3 py-2.5 text-[12px] text-gray-600">{row.salesperson || "—"}</td>
            <td className="px-3 py-2.5">
                <div className="flex items-center gap-2 flex-wrap">
                    <select value={product} onChange={(e) => setProduct(e.target.value)}
                        className="h-8 rounded-sm border border-gray-300 bg-white px-2 text-[12px]"
                        data-testid={`versand-open-product-${row.id}`}>
                        {POSTAGE_PRODUCTS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                    <Button size="sm" onClick={createLabel} disabled={busy || !addrOk}
                        className="h-8 bg-[#111827] hover:bg-orange-500 hover:text-[#111827] text-white font-bold text-[12px]"
                        data-testid={`versand-open-label-btn-${row.id}`}>
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Stamp className="w-3.5 h-3.5 mr-1" /> Label erstellen &amp; drucken</>}
                    </Button>
                    <button type="button"
                        onClick={async () => {
                            if (!window.confirm(`Versandauftrag „${row.title}" entfernen?\n\nDer Vorgang wird dauerhaft ausgeblendet (z. B. Testvorgang oder kein Versand nötig).`)) return;
                            try {
                                await api.post(`/dealer/shipping-cockpit/${row.id}/dismiss`);
                                toast.success("Versandauftrag entfernt");
                                onLabelCreated?.();
                            } catch (e) { toast.error(e.response?.data?.detail || "Entfernen fehlgeschlagen"); }
                        }}
                        className="h-8 px-2 rounded-sm border border-gray-300 text-gray-400 hover:border-red-400 hover:text-red-600 transition-colors"
                        title="Versandauftrag entfernen (Test / kein Versand nötig)"
                        data-testid={`versand-open-remove-${row.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                    </button>
                </div>
            </td>
        </tr>
    );
}

function ShippedRow({ row }) {
    const [busy, setBusy] = useState(false);
    const printLabel = async () => {
        setBusy(true);
        try {
            const r = await api.get(`/listings/${row.id}/dispo/papers-shipment/internetmarke/label.pdf`, { responseType: "blob" });
            previewPdfResponse(r, `Porto-Label_${row.tracking || row.id}.pdf`);
        } catch { toast.error("Label konnte nicht geladen werden"); }
        finally { setBusy(false); }
    };
    const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
    return (
        <tr className="border-b border-gray-100 hover:bg-orange-50/40 transition-colors" data-testid={`versand-shipped-row-${row.id}`}>
            <td className="px-3 py-2.5">
                <Link to={`/listing/${row.id}?dispo=1`} className="font-bold text-[#111827] hover:text-orange-600 hover:underline text-[13px]">{row.title || "Fahrzeug"}</Link>
                {row.contract_number && <div className="text-[11px] text-gray-500 font-mono-num">KV {row.contract_number}</div>}
            </td>
            <td className="px-3 py-2.5 text-[12px]">
                <div className="font-semibold text-gray-800">{row.customer?.name || "—"}</div>
                <div className="text-gray-600">{[row.customer?.postal_code, row.customer?.city].filter(Boolean).join(" ")}</div>
            </td>
            <td className="px-3 py-2.5 text-[12px] text-gray-600">
                <div>{row.carrier || "—"}</div>
                <div className="text-[11px] text-gray-500">{row.shipped_at ? fmtDate(row.shipped_at) : ""}</div>
            </td>
            <td className="px-3 py-2.5 text-[12px]">
                {row.tracking ? (
                    <a href={trackingUrl(row.tracking)} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 font-mono-num text-[#111827] hover:text-orange-600 hover:underline"
                        data-testid={`versand-shipped-tracking-${row.id}`}>
                        {row.tracking} <ExternalLink className="w-3 h-3" />
                    </a>
                ) : <span className="text-gray-400">—</span>}
            </td>
            <td className="px-3 py-2.5">
                {row.has_label && (
                    <Button size="sm" variant="outline" onClick={printLabel} disabled={busy}
                        className="h-8 text-[12px] font-bold border-gray-300 hover:bg-orange-500 hover:text-[#111827] hover:border-orange-500"
                        data-testid={`versand-shipped-print-btn-${row.id}`}>
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Printer className="w-3.5 h-3.5 mr-1" /> Label drucken</>}
                    </Button>
                )}
            </td>
        </tr>
    );
}

export default function VersandCockpitPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/dealer/shipping-cockpit");
            setData(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Versand-Cockpit konnte nicht geladen werden");
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    return (
        <div className="min-h-screen bg-gray-50" data-testid="versand-cockpit-page">
            <EmployeeContextBanner />
            <div className="bg-[#111827] text-white">
                <div className="max-w-6xl mx-auto px-4 py-6 flex items-center justify-between gap-4">
                    <div>
                        <h1 className="text-xl sm:text-2xl font-black flex items-center gap-2.5">
                            <Package className="w-6 h-6 text-orange-400" /> Versand-Cockpit
                        </h1>
                        <p className="text-[12px] text-gray-300 mt-1">Alle offenen Versandaufträge (Fahrzeugpapiere) — 1-Klick-Labeldruck &amp; Sendungsverfolgung, alle Standorte.</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={load} disabled={loading}
                        className="border-gray-500 bg-transparent text-white hover:bg-orange-500 hover:text-[#111827] hover:border-orange-500 h-9 font-bold"
                        data-testid="versand-refresh-btn">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4 mr-1.5" /> Aktualisieren</>}
                    </Button>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 py-6 space-y-8">
                <section data-testid="versand-open-section">
                    <h2 className="text-base md:text-lg font-black text-[#111827] flex items-center gap-2 mb-3">
                        <Truck className="w-5 h-5 text-orange-500" /> Offene Versandaufträge
                        <span className="px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 text-[11px] font-bold" data-testid="versand-open-count">{data?.open?.length ?? "…"}</span>
                    </h2>
                    <div className="bg-white rounded-sm border border-gray-200 overflow-x-auto">
                        {loading && !data ? (
                            <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
                        ) : (data?.open?.length ? (
                            <table className="w-full text-left">
                                <thead>
                                    <tr className="border-b border-gray-200 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                                        <th className="px-3 py-2">Fahrzeug</th>
                                        <th className="px-3 py-2">Empfänger (Käufer)</th>
                                        <th className="px-3 py-2">Verkäufer</th>
                                        <th className="px-3 py-2">Porto-Label</th>
                                    </tr>
                                </thead>
                                <tbody>{data.open.map((r) => <OpenRow key={r.id} row={r} onLabelCreated={load} />)}</tbody>
                            </table>
                        ) : (
                            <div className="p-8 text-center text-[13px] text-gray-500" data-testid="versand-open-empty">
                                <MailCheck className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                                Keine offenen Versandaufträge — alles versendet.
                            </div>
                        ))}
                    </div>
                </section>

                <section data-testid="versand-shipped-section">
                    <h2 className="text-base md:text-lg font-black text-[#111827] flex items-center gap-2 mb-3">
                        <MailCheck className="w-5 h-5 text-emerald-500" /> Versendet
                        <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold" data-testid="versand-shipped-count">{data?.shipped?.length ?? "…"}</span>
                    </h2>
                    <div className="bg-white rounded-sm border border-gray-200 overflow-x-auto">
                        {loading && !data ? (
                            <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
                        ) : (data?.shipped?.length ? (
                            <table className="w-full text-left">
                                <thead>
                                    <tr className="border-b border-gray-200 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                                        <th className="px-3 py-2">Fahrzeug</th>
                                        <th className="px-3 py-2">Empfänger</th>
                                        <th className="px-3 py-2">Versand</th>
                                        <th className="px-3 py-2">Tracking</th>
                                        <th className="px-3 py-2"></th>
                                    </tr>
                                </thead>
                                <tbody>{data.shipped.map((r) => <ShippedRow key={r.id} row={r} />)}</tbody>
                            </table>
                        ) : (
                            <div className="p-8 text-center text-[13px] text-gray-500" data-testid="versand-shipped-empty">Noch keine Sendungen erfasst.</div>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    );
}
